/**
 * Flows.js — Visual Chatbot Flow Builder
 * Drop-in replacement for the existing Flows.js page.
 * JSON stays as the backend format; users see a guided card-based UI.
 *
 * Bugs fixed vs v1:
 *  - handleImport: triple setEditing removed; clean single call
 *  - getEditData: unified — no more __import/__template split
 *  - editing state: simplified to null | 'new' | flowObject (with optional _draft for pre-filled new)
 *  - NodePalette: outside-click closes dropdown (useEffect + ref)
 *  - FlowPreview: null-guard when steps is empty
 *  - NodeEditor key: index-based so editing node.id doesn't remount card
 *  - set_variable: uses array-of-pairs internally to avoid duplicate-key loss
 *  - db_query + api_call added to emptyNode()
 *  - isBotMsg unused var removed
 *  - isNew prop removed (unused in FlowEditor)
 *  - handleSave: uses editing._isNew flag for POST vs PUT
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';
import { useNumbers } from '../context/NumberContext';
import { SAMPLE_FLOWS } from '../data/sampleFlows';

// ─────────────────────────────────────────────
// CONSTANTS & CONFIG
// ─────────────────────────────────────────────

const NODE_META = {
  message:       { icon: '💬', label: 'Message',       color: '#25D366', bg: 'rgba(37,211,102,0.08)',  desc: 'Send static text to user' },
  question:      { icon: '❓', label: 'Question',      color: '#60a5fa', bg: 'rgba(96,165,250,0.08)',  desc: 'Ask user for input / choice' },
  condition:     { icon: '🔀', label: 'Condition',     color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',  desc: 'Branch based on variable value' },
  set_variable:  { icon: '📌', label: 'Set Variable',  color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', desc: 'Assign session variables' },
  media:         { icon: '📎', label: 'Media',         color: '#f472b6', bg: 'rgba(244,114,182,0.08)', desc: 'Send image / PDF / video' },
  send_template: { icon: '📄', label: 'Template',      color: '#34d399', bg: 'rgba(52,211,153,0.08)',  desc: 'Send WhatsApp template message' },
  delay:         { icon: '⏱',  label: 'Delay',         color: '#94a3b8', bg: 'rgba(148,163,184,0.08)', desc: 'Pause flow for N seconds' },
  jump:          { icon: '🚀', label: 'Jump',          color: '#fb923c', bg: 'rgba(251,146,60,0.08)',  desc: 'Switch to another flow' },
  escalate:      { icon: '🙋', label: 'Escalate',      color: '#f87171', bg: 'rgba(248,113,113,0.08)', desc: 'Hand off to human agent' },
  end:           { icon: '🏁', label: 'End',           color: '#6b7280', bg: 'rgba(107,114,128,0.08)', desc: 'Close session with farewell' },

};

const INPUT_TYPES = ['choice', 'text', 'number', 'phone', 'email', 'date'];
const MEDIA_TYPES = ['image', 'document', 'video', 'audio'];
const SYSTEM_VARS = ['{{wa_id}}', '{{contact_name}}', '{{timestamp}}', '{{last_user_message}}'];

const TEMPLATES = [
  {
    name: 'Customer Support Bot',
    trigger_keyword: 'hi',
    icon: '🎧',
    desc: 'Menu → Support / Sales / Info with escalation',
    steps: [
      { id: 'start', type: 'set_variable', assignments: { name: '{{contact_name}}', phone: '{{wa_id}}' }, next: 'welcome' },
      { id: 'welcome', type: 'message', text: '👋 Hello {{name}}! Welcome to Support.\nHow can I help you today?', next: 'main_menu' },
      { id: 'main_menu', type: 'question', text: 'Please choose an option:\n\n1️⃣  Technical Support\n2️⃣  Sales Inquiry\n3️⃣  General Info\n4️⃣  Talk to Agent', variable: 'choice', input_type: 'choice',
        options: [
          { match: ['1','support','technical'], next: 'support_msg' },
          { match: ['2','sales','buy'], next: 'sales_msg' },
          { match: ['3','info','information'], next: 'info_msg' },
          { match: ['4','agent','human','help'], next: 'escalate' },
          { match: '*', reply: 'Please type 1, 2, 3, or 4.', next: 'main_menu' },
        ], fallback: "I didn't catch that. Please type 1–4.", max_retries: 3, on_max_retries: 'escalate' },
      { id: 'support_msg', type: 'message', text: '🔧 Our technical team will review your issue.\nPlease describe your problem in the next message.', next: 'ask_issue' },
      { id: 'ask_issue', type: 'question', text: 'Describe your issue briefly:', variable: 'issue', input_type: 'text', next: 'ticket_created' },
      { id: 'ticket_created', type: 'message', text: "✅ Ticket created! We'll get back to you within 24 hours.\nReference: #{{timestamp}}", next: 'end' },
      { id: 'sales_msg', type: 'message', text: '💼 Our sales team will contact you shortly!\nBusiness hours: Mon–Fri, 9am–6pm.', next: 'end' },
      { id: 'info_msg', type: 'message', text: 'ℹ️ Visit us at https://yourwebsite.com\nOr email: info@yourcompany.com', next: 'end' },
      { id: 'escalate', type: 'escalate', message: 'Connecting you to a human agent. Please hold! 🙏', end_flow: true },
      { id: 'end', type: 'end', text: 'Thanks for reaching out, {{name}}! 😊\n\nReply "hi" anytime to start again.' },
    ],
  },
  {
    name: 'Lead Generation Bot',
    trigger_keyword: 'start',
    icon: '🎯',
    desc: 'Collect name → email → requirement → save',
    steps: [
      { id: 'start', type: 'message', text: "👋 Hi! I'd love to learn more about you.\nThis will only take 2 minutes!", next: 'ask_name' },
      { id: 'ask_name', type: 'question', text: "What's your name?", variable: 'lead_name', input_type: 'text', next: 'ask_email' },
      { id: 'ask_email', type: 'question', text: 'Great, {{lead_name}}! 👍\nWhat\'s your email address?', variable: 'lead_email', input_type: 'email', next: 'ask_requirement', validation_error: 'Please enter a valid email address.' },
      { id: 'ask_requirement', type: 'question', text: 'What are you looking for? Tell us briefly:', variable: 'requirement', input_type: 'text', next: 'confirm' },
      { id: 'confirm', type: 'message', text: '✅ Got it!\n\n📋 Name: {{lead_name}}\n📧 Email: {{lead_email}}\n💬 Need: {{requirement}}\n\nOur team will reach out within 24 hours!', next: 'end' },
      { id: 'end', type: 'end', text: 'Thanks {{lead_name}}! We\'ll be in touch soon. 🚀' },
    ],
  },
];

// ─────────────────────────────────────────────
// HELPER: build empty node skeleton by type
// ─────────────────────────────────────────────
function emptyNode(type, existingIds = []) {
  const idBase = type.replace(/_/g, '-');
  let id = idBase;
  let i = 1;
  while (existingIds.includes(id)) { id = `${idBase}-${i++}`; }

  switch (type) {
    case 'message':       return { id, type, text: '', next: '' };
    case 'question':      return { id, type, text: '', variable: '', input_type: 'choice', options: [{ match: '*', next: '' }], fallback: "Sorry, I didn't understand.", max_retries: 3, on_max_retries: '' };
    case 'condition':     return { id, type, conditions: [{ if: '', next: '' }], default: '' };
    case 'set_variable':  return { id, type, assignments: { new_var: '' }, next: '' };
    case 'media':         return { id, type, media_type: 'image', url: '', caption: '', filename: '', next: '' };
    case 'send_template': return { id, type, template_name: '', language: 'en_US', components: [], next: '' };
    case 'delay':         return { id, type, seconds: 30, next: '' };
    case 'jump':          return { id, type, flow_name: '', preserve_session: true };
    case 'escalate':      return { id, type, message: 'Connecting you to a human agent... 🙏', tag_contact: '', notify_webhook: '', end_flow: true };
    case 'end':           return { id, type, text: 'Thank you! Have a great day 😊\n\nReply "hi" to start again.', clear_session: true };

    default:              return { id, type, next: '' };
  }
}

// ─────────────────────────────────────────────
// SMALL UI COMPONENTS
// ─────────────────────────────────────────────

function Pill({ color, bg, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: bg, color, border: `1px solid ${color}33`,
      borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600,
    }}>
      {children}
    </span>
  );
}

function Btn({ children, onClick, variant = 'secondary', disabled, style = {}, title }) {
  const variants = {
    primary:   { background: 'var(--accent)', color: '#000' },
    secondary: { background: 'var(--bg3)', color: 'var(--text)', border: '1px solid var(--border)' },
    danger:    { background: 'rgba(239,68,68,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)' },
    ghost:     { background: 'transparent', color: 'var(--text2)', border: '1px solid transparent', padding: '4px 8px', fontSize: 12 },
  };
  return (
    <button
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        border: 'none', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'var(--sans)', fontWeight: 600, transition: 'all 0.15s',
        opacity: disabled ? 0.45 : 1, padding: '7px 14px', fontSize: 13,
        ...variants[variant], ...style,
      }}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
          {hint && <span style={{ fontWeight: 400, color: 'var(--text3)', marginLeft: 6, textTransform: 'none', fontSize: 11 }}>{hint}</span>}
        </label>
      )}
      {children}
      {error && <span style={{ fontSize: 11, color: 'var(--danger)' }}>⚠ {error}</span>}
    </div>
  );
}

function Input({ value, onChange, placeholder, mono, rows }) {
  const style = {
    background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
    color: 'var(--text)', padding: '8px 12px', fontSize: 13, width: '100%',
    fontFamily: mono ? 'var(--mono)' : 'var(--sans)', outline: 'none',
    resize: rows ? 'vertical' : undefined,
  };
  if (rows) {
    return <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} style={style} />;
  }
  return <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={style} />;
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
        color: 'var(--text)', padding: '8px 12px', fontSize: 13, width: '100%', outline: 'none',
      }}
    >
      {options.map(o => (
        <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
          {typeof o === 'string' ? o : o.label}
        </option>
      ))}
    </select>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', fontSize: 13 }}>
      <span style={{
        position: 'relative', width: 36, height: 20, borderRadius: 10, flexShrink: 0,
        background: checked ? 'var(--accent)' : 'var(--bg4)', transition: 'background 0.2s',
        display: 'inline-block',
      }}>
        <span style={{
          position: 'absolute', top: 3, left: checked ? 19 : 3, width: 14, height: 14,
          borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
        }} />
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
      </span>
      <span style={{ color: 'var(--text2)' }}>{label}</span>
    </label>
  );
}

// ─────────────────────────────────────────────
// NODE EDITOR — type-specific form fields
// ─────────────────────────────────────────────

function NodeEditor({ node, onChange, onDelete, onMoveUp, onMoveDown, isFirst, isLast, index }) {
  const meta = NODE_META[node.type] || { icon: '⚙', label: node.type, color: '#888', bg: 'rgba(136,136,136,0.08)' };
  const [collapsed, setCollapsed] = useState(false);

  const upd = (patch) => onChange({ ...node, ...patch });

  // set_variable: work with array of pairs to avoid duplicate-key loss
  const assignPairs = Object.entries(node.assignments || {});
  const setAssignPairs = (pairs) => upd({ assignments: Object.fromEntries(pairs) });

  const renderBody = () => {
    switch (node.type) {

      case 'message': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Message Text" hint="{{variables}} supported">
            <Input value={node.text || ''} onChange={v => upd({ text: v })} placeholder="Hello {{contact_name}}! 👋" rows={3} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Next Step" hint="node id">
              <Input value={node.next || ''} onChange={v => upd({ next: v })} placeholder="main_menu" />
            </Field>
            <Field label="Delay ms" hint="optional typing feel">
              <Input value={node.delay_ms ?? ''} onChange={v => upd({ delay_ms: v ? Number(v) : undefined })} placeholder="500" />
            </Field>
          </div>
        </div>
      );

      case 'question': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Question Text">
            <Input value={node.text || ''} onChange={v => upd({ text: v })} rows={3}
              placeholder={'Choose an option:\n\n1️⃣ Support\n2️⃣ Sales'} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Save Answer As">
              <Input value={node.variable || ''} onChange={v => upd({ variable: v })} placeholder="user_choice" />
            </Field>
            <Field label="Input Type">
              <Select value={node.input_type || 'choice'} onChange={v => upd({ input_type: v })} options={INPUT_TYPES} />
            </Field>
          </div>
          {(node.input_type === 'choice' || !node.input_type) && (
            <Field label="Options" hint="match keywords → next step">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {(node.options || []).map((opt, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Input
                      value={Array.isArray(opt.match) ? opt.match.join(', ') : (opt.match || '')}
                      onChange={v => {
                        const opts = [...(node.options || [])];
                        const vals = v.split(',').map(x => x.trim()).filter(Boolean);
                        opts[i] = { ...opts[i], match: vals.length === 1 ? vals[0] : vals };
                        upd({ options: opts });
                      }}
                      placeholder={i === (node.options || []).length - 1 ? '* (catch-all)' : '1, order, track'}
                    />
                    <span style={{ color: 'var(--text3)', fontSize: 18, flexShrink: 0 }}>→</span>
                    <Input
                      value={opt.next || ''}
                      onChange={v => {
                        const opts = [...(node.options || [])];
                        opts[i] = { ...opts[i], next: v };
                        upd({ options: opts });
                      }}
                      placeholder="next_step_id"
                    />
                    <Btn variant="ghost" onClick={() => {
                      const opts = [...(node.options || [])];
                      opts.splice(i, 1);
                      upd({ options: opts });
                    }} style={{ flexShrink: 0, color: 'var(--danger)' }}>✕</Btn>
                  </div>
                ))}
                <Btn variant="ghost" onClick={() => upd({ options: [...(node.options || []), { match: '', next: '' }] })}
                  style={{ color: 'var(--accent)', padding: '4px 0' }}>
                  + Add Option
                </Btn>
              </div>
            </Field>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Fallback Message">
              <Input value={node.fallback || ''} onChange={v => upd({ fallback: v })} placeholder="Sorry, please try again." />
            </Field>
            <Field label="Validation Error" hint="typed inputs only">
              <Input value={node.validation_error || ''} onChange={v => upd({ validation_error: v })} placeholder="Enter a valid email." />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Max Retries">
              <Input value={node.max_retries ?? 3} onChange={v => upd({ max_retries: Number(v) })} placeholder="3" />
            </Field>
            <Field label="On Max Retries → node id">
              <Input value={node.on_max_retries || ''} onChange={v => upd({ on_max_retries: v })} placeholder="escalate" />
            </Field>
          </div>
        </div>
      );

      case 'condition': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Branches" hint="evaluated top to bottom">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {(node.conditions || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ color: 'var(--text3)', fontSize: 11, flexShrink: 0 }}>if</span>
                  <Input value={c.if || ''} onChange={v => {
                    const cc = [...(node.conditions || [])];
                    cc[i] = { ...cc[i], if: v };
                    upd({ conditions: cc });
                  }} placeholder="{{order_status}} == 'delivered'" />
                  <span style={{ color: 'var(--text3)', fontSize: 18, flexShrink: 0 }}>→</span>
                  <Input value={c.next || ''} onChange={v => {
                    const cc = [...(node.conditions || [])];
                    cc[i] = { ...cc[i], next: v };
                    upd({ conditions: cc });
                  }} placeholder="delivered_msg" />
                  <Btn variant="ghost" onClick={() => {
                    const cc = [...(node.conditions || [])];
                    cc.splice(i, 1);
                    upd({ conditions: cc });
                  }} style={{ color: 'var(--danger)', flexShrink: 0 }}>✕</Btn>
                </div>
              ))}
              <Btn variant="ghost" onClick={() => upd({ conditions: [...(node.conditions || []), { if: '', next: '' }] })}
                style={{ color: 'var(--accent)', padding: '4px 0' }}>
                + Add Branch
              </Btn>
            </div>
          </Field>
          <Field label="Default (no match) → node id">
            <Input value={node.default || ''} onChange={v => upd({ default: v })} placeholder="fallback_msg" />
          </Field>
        </div>
      );

      case 'set_variable': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Assignments" hint="variable = value">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {assignPairs.map(([k, v], i) => (
                <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Input value={k} onChange={newK => {
                    const pairs = [...assignPairs];
                    pairs[i] = [newK, v];
                    setAssignPairs(pairs);
                  }} placeholder="variable_name" />
                  <span style={{ color: 'var(--text3)', fontSize: 14, flexShrink: 0 }}>=</span>
                  <Input value={String(v)} onChange={newV => {
                    const pairs = [...assignPairs];
                    pairs[i] = [k, newV];
                    setAssignPairs(pairs);
                  }} placeholder="{{wa_id}} or literal value" />
                  <Btn variant="ghost" onClick={() => {
                    const pairs = [...assignPairs];
                    pairs.splice(i, 1);
                    setAssignPairs(pairs);
                  }} style={{ color: 'var(--danger)', flexShrink: 0 }}>✕</Btn>
                </div>
              ))}
              <Btn variant="ghost" onClick={() => setAssignPairs([...assignPairs, [`var_${assignPairs.length + 1}`, '']])}
                style={{ color: 'var(--accent)', padding: '4px 0' }}>
                + Add Variable
              </Btn>
            </div>
          </Field>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {SYSTEM_VARS.map(sv => (
              <span key={sv} style={{
                fontSize: 11, background: 'var(--bg4)', color: 'var(--accent2)',
                padding: '2px 7px', borderRadius: 4, fontFamily: 'var(--mono)', cursor: 'default',
              }}>{sv}</span>
            ))}
          </div>
          <Field label="Next Step">
            <Input value={node.next || ''} onChange={v => upd({ next: v })} placeholder="welcome" />
          </Field>
        </div>
      );

      case 'media': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Media Type">
              <Select value={node.media_type || 'image'} onChange={v => upd({ media_type: v })} options={MEDIA_TYPES} />
            </Field>
            <Field label="Filename" hint="for documents">
              <Input value={node.filename || ''} onChange={v => upd({ filename: v })} placeholder="catalogue.pdf" />
            </Field>
          </div>
          <Field label="URL">
            <Input value={node.url || ''} onChange={v => upd({ url: v })} placeholder="https://yoursite.com/file.pdf" />
          </Field>
          <Field label="Caption" hint="optional">
            <Input value={node.caption || ''} onChange={v => upd({ caption: v })} placeholder="Here's our product catalogue! 📄" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Next Step">
              <Input value={node.next || ''} onChange={v => upd({ next: v })} placeholder="after_media" />
            </Field>
            <Field label="On Error → node id">
              <Input value={node.on_error || ''} onChange={v => upd({ on_error: v })} placeholder="media_error_msg" />
            </Field>
          </div>
        </div>
      );

      case 'send_template': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Template Name">
              <Input value={node.template_name || ''} onChange={v => upd({ template_name: v })} placeholder="order_confirmation" />
            </Field>
            <Field label="Language">
              <Input value={node.language || 'en_US'} onChange={v => upd({ language: v })} placeholder="en_US" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Next Step">
              <Input value={node.next || ''} onChange={v => upd({ next: v })} placeholder="post_template" />
            </Field>
            <Field label="On Error → node id">
              <Input value={node.on_error || ''} onChange={v => upd({ on_error: v })} placeholder="template_error" />
            </Field>
          </div>
        </div>
      );

      case 'delay': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Wait Duration (seconds)">
            <Input value={node.seconds ?? 30} onChange={v => upd({ seconds: Number(v) })} placeholder="300" />
          </Field>
          <Field label="Next Step">
            <Input value={node.next || ''} onChange={v => upd({ next: v })} placeholder="followup_message" />
          </Field>
        </div>
      );

      case 'jump': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Target Flow Name" hint="exact name of the other flow">
            <Input value={node.flow_name || ''} onChange={v => upd({ flow_name: v })} placeholder="returns_and_refunds_flow" />
          </Field>
          <Toggle checked={node.preserve_session !== false} onChange={v => upd({ preserve_session: v })} label="Preserve current session data" />
        </div>
      );

      case 'escalate': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Message to User">
            <Input value={node.message || ''} onChange={v => upd({ message: v })} rows={2} placeholder="Connecting you to a human agent... 🙏" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Tag Contact" hint="for inbox filtering">
              <Input value={node.tag_contact || ''} onChange={v => upd({ tag_contact: v })} placeholder="needs_human" />
            </Field>
            <Field label="Notify Webhook" hint="optional POST">
              <Input value={node.notify_webhook || ''} onChange={v => upd({ notify_webhook: v })} placeholder="https://your-crm.com/webhook" />
            </Field>
          </div>
          <Toggle checked={node.end_flow !== false} onChange={v => upd({ end_flow: v })} label="End flow session" />
        </div>
      );

      case 'end': return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Farewell Message">
            <Input value={node.text || ''} onChange={v => upd({ text: v })} rows={3}
              placeholder={'Thank you! Have a great day 😊\n\nReply "hi" to start again.'} />
          </Field>
          <Toggle checked={node.clear_session !== false} onChange={v => upd({ clear_session: v })} label="Clear session data" />
        </div>
      );

      default: return (
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'var(--mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          {JSON.stringify(node, null, 2)}
        </div>
      );
    }
  };

  return (
    <div style={{
      border: `1px solid ${collapsed ? 'var(--border)' : meta.color + '44'}`,
      borderRadius: 10, overflow: 'hidden',
      boxShadow: collapsed ? 'none' : `0 0 0 1px ${meta.color}11`,
      transition: 'border-color 0.2s',
    }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
          background: collapsed ? 'var(--bg3)' : meta.bg,
          cursor: 'pointer', userSelect: 'none',
        }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--text3)', flexShrink: 0, minWidth: 20, textAlign: 'right' }}>
          {String(index + 1).padStart(2, '0')}
        </span>
        <span style={{ fontSize: 16 }}>{meta.icon}</span>
        <Pill color={meta.color} bg={meta.bg}>{meta.label}</Pill>
        <span style={{
          fontFamily: 'var(--mono)', fontSize: 12, color: meta.color, fontWeight: 700,
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          #{node.id}
        </span>
        {node.next && (
          <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>
            → <code style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{node.next}</code>
          </span>
        )}
        <span style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 4 }}>{collapsed ? '▼' : '▲'}</span>
      </div>

      {/* Body */}
      {!collapsed && (
        <div style={{ padding: 16, background: 'var(--bg2)', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Node ID" hint="other nodes reference this in their 'next' field">
            <Input value={node.id} onChange={v => onChange({ ...node, id: v })} mono placeholder="welcome_message" />
          </Field>
          <div style={{ height: 1, background: 'var(--border)' }} />
          {renderBody()}
          <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Btn variant="ghost" onClick={onMoveUp}  disabled={isFirst}  title="Move up">↑</Btn>
            <Btn variant="ghost" onClick={onMoveDown} disabled={isLast}  title="Move down">↓</Btn>
            <Btn variant="danger" onClick={onDelete}  style={{ fontSize: 12 }}>🗑 Remove</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// FLOW PREVIEW — conversation as chat bubbles
// ─────────────────────────────────────────────

function FlowPreview({ steps }) {
  if (!steps || steps.length === 0) {
    return (
      <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
        Add some nodes first to see the preview.
      </div>
    );
  }

  const getNodeChain = () => {
    const map = {};
    steps.forEach(s => { map[s.id] = s; });
    const chain = [];
    let current = steps[0];
    const visited = new Set();
    while (current && !visited.has(current.id) && chain.length < 20) {
      visited.add(current.id);
      chain.push(current);
      if (['end', 'escalate'].includes(current.type)) break;
      const nextId = current.next || (current.options && current.options[0] && current.options[0].next);
      current = nextId ? map[nextId] : null;
    }
    return chain;
  };

  const chain = getNodeChain();

  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: 16, maxHeight: 420, overflowY: 'auto' }}>
      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>
        Preview — first path through flow
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {chain.map((node, i) => {
          const meta = NODE_META[node.type] || { icon: '⚙', color: '#888', bg: 'rgba(136,136,136,0.08)' };
          if (['set_variable', 'condition', 'delay', 'jump'].includes(node.type)) {
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
                <span style={{ fontSize: 11, color: meta.color, padding: '2px 10px', border: `1px solid ${meta.color}44`, borderRadius: 20 }}>
                  {meta.icon} {node.id}
                </span>
                <div style={{ height: 1, flex: 1, background: 'var(--border)' }} />
              </div>
            );
          }
          const text = node.text || node.message || '';
          return (
            <div key={i} style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: meta.bg, border: `1px solid ${meta.color}44`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
              }}>
                {meta.icon}
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3 }}>{meta.label} · #{node.id}</div>
                <div style={{
                  background: 'var(--bg4)', borderRadius: '4px 12px 12px 12px',
                  padding: '8px 12px', fontSize: 13, maxWidth: 300,
                  lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  color: text ? 'var(--text)' : 'var(--text3)', fontStyle: text ? 'normal' : 'italic',
                }}>
                  {text || '(no text set)'}
                </div>
                {node.type === 'question' && node.options && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {node.options.filter(o => o.match !== '*').slice(0, 4).map((o, oi) => (
                      <span key={oi} style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 12,
                        border: '1px solid var(--accent2)', color: 'var(--accent)',
                      }}>
                        {Array.isArray(o.match) ? o.match[0] : o.match}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {chain.length >= 20 && (
          <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 12 }}>… flow continues</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// ADD NODE PALETTE — with outside-click close
// ─────────────────────────────────────────────

const EXTRA_NODES = [

];

function NodePalette({ onAdd }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allTypes = [
    ...Object.entries(NODE_META).map(([type, m]) => ({ type, ...m })),
    ...EXTRA_NODES.filter(e => !NODE_META[e.type]), // avoid dupes since we moved extra into NODE_META
  ];

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <Btn variant="secondary" onClick={() => setOpen(!open)}>＋ Add Node</Btn>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 6,
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
          padding: 10, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6,
        }}>
          {Object.entries(NODE_META).map(([type, m]) => (
            <button
              key={type}
              onClick={() => { onAdd(type); setOpen(false); }}
              style={{
                background: m.bg, border: `1px solid ${m.color}33`, borderRadius: 7,
                color: 'var(--text)', padding: '8px 10px', cursor: 'pointer', textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: 3,
              }}
            >
              <span style={{ fontSize: 18 }}>{m.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: m.color }}>{m.label}</span>
              <span style={{ fontSize: 10, color: 'var(--text3)', lineHeight: 1.3 }}>{m.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TEMPLATE PICKER
// ─────────────────────────────────────────────

function TemplatePicker({ onSelect, onClose }) {
  const [hovered, setHovered] = React.useState(null);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
        zIndex: 200, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: '20px 16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 16, width: '100%', maxWidth: 680,
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 24px', borderBottom: '1px solid var(--border)',
          position: 'sticky', top: 0, background: 'var(--bg2)', zIndex: 1,
        }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.06em', marginBottom: 4 }}>
              CHOOSE A TEMPLATE
            </div>
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>
              Pick a ready-made flow, change the trigger word, customise messages, then save.
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'var(--bg3)', border: '1px solid var(--border)',
              borderRadius: 8, width: 32, height: 32, cursor: 'pointer',
              color: 'var(--text)', fontSize: 16, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Grid */}
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {SAMPLE_FLOWS.map((t, i) => {
            const stepCount = t.flow_json?.steps?.length || 0;
            const nodeTypes = [...new Set((t.flow_json?.steps || []).map(s => s.type))];
            const isHov = hovered === i;
            return (
              <button
                key={t.name}
                onClick={() => { onSelect(t); onClose(); }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  width: '100%', background: isHov ? 'rgba(37,211,102,0.05)' : 'var(--bg3)',
                  border: `1px solid ${isHov ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 10, padding: '14px 16px',
                  cursor: 'pointer', textAlign: 'left', color: 'var(--text)',
                  transition: 'all 0.15s', display: 'flex', gap: 16, alignItems: 'flex-start',
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 48, height: 48, borderRadius: 12, flexShrink: 0,
                  background: isHov ? 'rgba(37,211,102,0.12)' : 'var(--bg2)',
                  border: `1px solid ${isHov ? 'rgba(37,211,102,0.3)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, transition: 'all 0.15s',
                }}>
                  {t.icon}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: isHov ? 'var(--accent)' : 'var(--text)' }}>
                      {t.name}
                    </span>
                    <span style={{
                      fontSize: 10, fontFamily: 'var(--mono)', padding: '2px 7px',
                      borderRadius: 4, background: 'rgba(37,211,102,0.1)',
                      color: 'var(--accent)', border: '1px solid rgba(37,211,102,0.2)',
                    }}>
                      trigger: "{t.trigger_keyword}"
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, marginBottom: 8 }}>
                    {t.description}
                  </div>
                  {/* Node type pills */}
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'var(--text3)', marginRight: 2 }}>{stepCount} steps:</span>
                    {nodeTypes.map(nt => {
                      const m = NODE_META[nt] || { icon: '⚙', color: '#888', bg: 'rgba(136,136,136,0.08)' };
                      return (
                        <span key={nt} style={{
                          fontSize: 10, padding: '1px 6px', borderRadius: 10,
                          background: m.bg, color: m.color, border: `1px solid ${m.color}33`,
                        }}>
                          {m.icon} {nt}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Arrow */}
                <div style={{
                  color: isHov ? 'var(--accent)' : 'var(--text3)',
                  fontSize: 18, flexShrink: 0, alignSelf: 'center',
                  transition: 'color 0.15s',
                }}>→</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// FLOW EDITOR — full visual form editor
// ─────────────────────────────────────────────

function FlowEditor({ initialFlow, initialName, initialTrigger, onSave, onCancel }) {
  const [name, setName]           = useState(initialName || '');
  const [trigger, setTrigger]     = useState(initialTrigger || '');
  const [steps, setSteps]         = useState(() => {
    try { const p = JSON.parse(initialFlow || '{}'); return Array.isArray(p.steps) ? p.steps : []; }
    catch { return []; }
  });
  const [activeTab, setActiveTab] = useState('visual');
  const [saving, setSaving]       = useState(false);
  const [jsonText, setJsonText]   = useState('');
  const [jsonError, setJsonError] = useState('');
  const [nameError, setNameError] = useState('');

  // Sync JSON editor when switching to json tab
  useEffect(() => {
    if (activeTab === 'json') {
      setJsonText(JSON.stringify({ steps }, null, 2));
      setJsonError('');
    }
  }, [activeTab]); // intentionally only on tab switch

  const updateNode = useCallback((idx, newNode) => {
    setSteps(prev => { const s = [...prev]; s[idx] = newNode; return s; });
  }, []);

  const deleteNode = (idx) => setSteps(prev => prev.filter((_, i) => i !== idx));

  const moveNode = (idx, dir) => {
    setSteps(prev => {
      const s = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= s.length) return s;
      [s[idx], s[target]] = [s[target], s[idx]];
      return s;
    });
  };

  const addNode = (type) => {
    const ids = steps.map(s => s.id);
    setSteps(prev => [...prev, emptyNode(type, ids)]);
  };

  const applyJsonEdit = () => {
    try {
      const parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed.steps)) throw new Error('JSON must have a "steps" array');
      setSteps(parsed.steps);
      setJsonError('');
      setActiveTab('visual');
    } catch (e) {
      setJsonError(e.message);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { setNameError('Flow name is required'); return; }
    if (steps.length === 0) { alert('Add at least one node before saving.'); return; }
    setNameError('');
    const flow_json = JSON.stringify({ steps }, null, 2);
    setSaving(true);
    await onSave({ name: name.trim(), trigger_keyword: trigger.trim(), flow_json });
    setSaving(false);
  };

  const TAB = ({ id, label }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        background: activeTab === id ? 'var(--bg3)' : 'transparent',
        border: activeTab === id ? '1px solid var(--border)' : '1px solid transparent',
        borderRadius: 7, padding: '6px 16px',
        color: activeTab === id ? 'var(--text)' : 'var(--text2)',
        cursor: 'pointer', fontSize: 13, fontWeight: activeTab === id ? 600 : 400,
        fontFamily: 'var(--sans)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      {/* Meta row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <Field label="Flow Name" error={nameError}>
          <Input value={name} onChange={v => { setName(v); if (v.trim()) setNameError(''); }} placeholder="Customer Support Bot" />
        </Field>
        <Field label="Trigger Keyword" hint="user types this to start the bot">
          <Input value={trigger} onChange={setTrigger} placeholder="hi, start, hello" />
        </Field>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          <TAB id="visual" label="🃏 Builder" />
          <TAB id="preview" label="👁 Preview" />
          <TAB id="json"    label="{ } JSON" />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {activeTab === 'json' && (
            <Btn variant="secondary" onClick={applyJsonEdit} style={{ fontSize: 12 }}>
              Apply JSON →
            </Btn>
          )}
          {activeTab === 'visual' && <NodePalette onAdd={addNode} />}
        </div>
      </div>

      {/* Visual builder */}
      {activeTab === 'visual' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {steps.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '48px 24px',
              background: 'var(--bg3)', borderRadius: 10, border: '2px dashed var(--border)',
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⚡</div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>No nodes yet</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>Click "Add Node" above to start building</div>
            </div>
          ) : (
            steps.map((node, i) => (
              <NodeEditor
                key={i}  // index-based: prevents remount when user edits node.id
                node={node}
                index={i}
                onChange={n => updateNode(i, n)}
                onDelete={() => deleteNode(i)}
                onMoveUp={() => moveNode(i, -1)}
                onMoveDown={() => moveNode(i, 1)}
                isFirst={i === 0}
                isLast={i === steps.length - 1}
              />
            ))
          )}
          {steps.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
              <NodePalette onAdd={addNode} />
            </div>
          )}
        </div>
      )}

      {/* Preview */}
      {activeTab === 'preview' && <FlowPreview steps={steps} />}

      {/* JSON editor */}
      {activeTab === 'json' && (
        <div>
          {jsonError && <div style={{ marginBottom: 8, color: 'var(--danger)', fontSize: 12 }}>⚠ {jsonError}</div>}
          <textarea
            value={jsonText}
            onChange={e => { setJsonText(e.target.value); setJsonError(''); }}
            rows={24}
            style={{
              background: 'var(--bg3)', border: `1px solid ${jsonError ? 'var(--danger)' : 'var(--border)'}`,
              borderRadius: 8, color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 12,
              padding: 14, width: '100%', resize: 'vertical', lineHeight: 1.6, outline: 'none',
            }}
          />
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
            Edit JSON directly, then click "Apply JSON →" to sync with the visual builder.
          </div>
        </div>
      )}

      {/* Save bar */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
        <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : `💾 Save Flow (${steps.length} node${steps.length !== 1 ? 's' : ''})`}
        </Btn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────

export default function Flows() {
  const { activeNumber } = useNumbers();
  const [flows, setFlows]               = useState([]);
  const [editing, setEditing]           = useState(null); // null | { _isNew, name, trigger_keyword, flow_json, id? }
  const [showTemplates, setShowTemplates] = useState(false);
  const [toast, setToast]               = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const importRef                       = useRef();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadFlows = useCallback(async () => {
    try {
      const r = await api.get('/flows' + (activeNumber ? '?wa_number_id=' + activeNumber.id : ''));
      setFlows(r.data);
    } catch (e) {
      console.error('Failed to load flows', e);
    }
  }, [activeNumber]);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  // Unified save: checks editing._isNew for POST vs PUT
  const handleSave = async ({ name, trigger_keyword, flow_json }) => {
    try {
      if (editing && !editing._isNew) {
        await api.put(`/flows/${editing.id}`, { name, trigger_keyword, flow_json });
        showToast(`✅ "${name}" updated!`);
      } else {
        await api.post('/flows', { wa_number_id: activeNumber?.id, name, trigger_keyword, flow_json });
        showToast(`✅ "${name}" created!`);
      }
      setEditing(null);
      loadFlows();
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed. Please try again.');
    }
  };

  const toggleActive = async (f) => {
    try {
      await api.put(`/flows/${f.id}`, { is_active: f.is_active ? 0 : 1 });
      loadFlows();
      showToast(f.is_active ? `"${f.name}" paused` : `"${f.name}" activated`);
    } catch (e) {
      showToast('Failed to update flow status', 'error');
    }
  };

  const deleteFlow = async (id) => {
    try {
      await api.delete(`/flows/${id}`);
      setConfirmDelete(null);
      loadFlows();
      showToast('Flow deleted', 'error');
    } catch (e) {
      showToast('Delete failed', 'error');
    }
  };

  const exportFlow = (f) => {
    let parsed;
    try { parsed = JSON.parse(f.flow_json); } catch { parsed = { steps: [] }; }
    const blob = new Blob(
      [JSON.stringify({ _flow_name: f.name, _trigger_keyword: f.trigger_keyword || '', ...parsed }, null, 2)],
      { type: 'application/json' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${f.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`Exported: ${f.name}`);
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const name    = parsed._flow_name || file.name.replace(/\.json$/i, '');
        const trigger = parsed._trigger_keyword || '';
        const { _flow_name, _trigger_keyword, ...flowBody } = parsed;
        setEditing({ _isNew: true, name, trigger_keyword: trigger, flow_json: JSON.stringify(flowBody, null, 2) });
        showToast(`Imported: ${name}`);
      } catch (err) {
        alert('Invalid JSON file: ' + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleTemplateSelect = (t) => {
    // SAMPLE_FLOWS have flow_json as object; old format had .steps array
    const flowObj = t.flow_json ? t.flow_json : { steps: t.steps || [] };
    setEditing({
      _isNew: true,
      name: t.name,
      trigger_keyword: t.trigger_keyword,
      flow_json: JSON.stringify(flowObj, null, 2),
    });
    setShowTemplates(false);
    showToast(`✅ Template loaded: ${t.name}. Customise and save!`);
  };

  // ── Editor view ──
  if (editing) {
    return (
      <div style={{ padding: '20px 16px', maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <Btn variant="ghost" onClick={() => setEditing(null)} style={{ padding: '6px 10px', fontSize: 18, lineHeight: 1 }}>←</Btn>
          <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text2)', letterSpacing: '0.06em', margin: 0 }}>
            {editing._isNew ? '// NEW FLOW' : `// EDIT: ${editing.name}`}
          </h2>
        </div>
        <FlowEditor
          key={editing._isNew ? 'new' : editing.id}
          initialName={editing.name}
          initialTrigger={editing.trigger_keyword}
          initialFlow={editing.flow_json || '{"steps":[]}'}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  // ── List view ──
  return (
    <div style={{ padding: '20px 16px', maxWidth: 760, margin: '0 auto' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          background: toast.type === 'error' ? 'var(--danger)' : 'var(--accent)',
          color: '#000', padding: '10px 18px', borderRadius: 8,
          fontSize: 13, fontWeight: 700, boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Template picker modal */}
      {showTemplates && (
        <TemplatePicker
          onSelect={handleTemplateSelect}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
            padding: 28, maxWidth: 360, width: '100%', textAlign: 'center',
            margin: '0 16px',
          }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🗑</div>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              Delete "{confirmDelete.name}"?
            </div>
            <div style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 24 }}>
              This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <Btn variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={() => deleteFlow(confirmDelete.id)}>Delete</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text2)', letterSpacing: '0.06em', marginBottom: 4 }}>
            BOT FLOWS {flows.length > 0 && `(${flows.length})`}
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text3)' }}>Visual chatbot builder — no JSON required</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input ref={importRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleImport} />
          <Btn variant="secondary" onClick={() => importRef.current?.click()} style={{ fontSize: 12 }}>📥 Import</Btn>
          <Btn variant="secondary" onClick={() => setShowTemplates(true)} style={{ fontSize: 12 }}>⚡ Templates</Btn>
          <Btn variant="primary" onClick={() => setEditing({ _isNew: true, name: '', trigger_keyword: '', flow_json: '{"steps":[]}' })}>
            + New Flow
          </Btn>
        </div>
      </div>

      {/* Flows list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {flows.map(f => {
          let nodeCount = 0;
          let nodeTypes = [];
          try {
            const parsed = JSON.parse(f.flow_json);
            nodeCount = parsed.steps?.length || 0;
            nodeTypes = [...new Set((parsed.steps || []).map(s => s.type))];
          } catch {}

          return (
            <div
              key={f.id}
              style={{
                background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12,
                padding: '16px 18px', transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                  background: f.is_active ? 'rgba(37,211,102,0.1)' : 'var(--bg3)',
                  border: `1px solid ${f.is_active ? 'rgba(37,211,102,0.3)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
                }}>
                  🤖
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{f.name}</span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                      background: f.is_active ? 'rgba(37,211,102,0.12)' : 'var(--bg3)',
                      color: f.is_active ? 'var(--accent)' : 'var(--text3)',
                      border: `1px solid ${f.is_active ? 'rgba(37,211,102,0.3)' : 'var(--border)'}`,
                    }}>
                      {f.is_active ? '● Active' : '○ Paused'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text3)', flexWrap: 'wrap' }}>
                    <span>
                      Trigger:{' '}
                      <code style={{ color: 'var(--accent2)', fontFamily: 'var(--mono)', fontSize: 11 }}>
                        {f.trigger_keyword || '(none)'}
                      </code>
                    </span>
                    <span>{nodeCount} node{nodeCount !== 1 ? 's' : ''}</span>
                  </div>
                  {nodeTypes.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                      {nodeTypes.map(t => {
                        const m = NODE_META[t] || { icon: '⚙', color: '#888', bg: 'rgba(136,136,136,0.08)' };
                        return (
                          <span key={t} style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 12,
                            background: m.bg, color: m.color, border: `1px solid ${m.color}33`,
                          }}>
                            {m.icon} {t}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
                <Btn
                  variant="primary"
                  onClick={() => setEditing({ ...f, flow_json: f.flow_json, _isNew: false })}
                  style={{ fontSize: 12, padding: '6px 14px' }}
                >
                  ✏ Edit
                </Btn>
                <Btn variant="secondary" onClick={() => toggleActive(f)} style={{ fontSize: 12 }}>
                  {f.is_active ? '⏸ Pause' : '▶ Activate'}
                </Btn>
                <Btn variant="secondary" onClick={() => exportFlow(f)} style={{ fontSize: 12 }}>📤 Export</Btn>
                <Btn variant="danger" onClick={() => setConfirmDelete(f)} style={{ fontSize: 12 }}>🗑</Btn>
              </div>
            </div>
          );
        })}

        {flows.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            background: 'var(--bg2)', borderRadius: 12, border: '2px dashed var(--border)',
          }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🤖</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No flows yet</div>
            <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24 }}>
              Build a chatbot in minutes with the visual editor
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Btn variant="primary" onClick={() => setShowTemplates(true)}>⚡ Start from Template</Btn>
              <Btn variant="secondary" onClick={() => setEditing({ _isNew: true, name: '', trigger_keyword: '', flow_json: '{"steps":[]}' })}>
                + Build from Scratch
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
